import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import Swal from 'sweetalert2';
import { AutorizacionesService } from '../../core/services/autorizaciones.service';
import { documentNumberValidator, onlyLettersValidator, phoneNumberValidator, dateNotInPastValidator } from './custom-validators';
import { FormularioUtils } from './formulario-utils';


@Component({
  selector: 'app-formulario',
  templateUrl: './formulario.component.html',
  styleUrls: ['./formulario.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [AutorizacionesService]
})

export class FormularioComponent implements OnInit {
  // ==========================================
  // PROPIEDADES
  // ==========================================
  title = 'Mis Autorizaciones';
  authForm!: FormGroup;
  submitted = false;
  loading = false;
  uploadProgress = 0;
  farmacias: any[] = [];
  instituciones: any[] = [];
  autorizaciones: any[] = [];
  fechaMinima: string = new Date().toISOString().split('T')[0];
  documentoValidado = false;

  readonly MAX_TOTAL_FILE_SIZE = 10 * 1024 * 1024;

  // ==========================================
  // GESTIÓN DE ARCHIVOS
  // ==========================================
  // Referencias a los inputs de tipo file
  @ViewChild('recetasFilesInput') recetasFilesInput!: ElementRef;
  @ViewChild('pedidosEstFilesInput') pedidosEstFilesInput!: ElementRef;
  @ViewChild('pedidosCirFilesInput') pedidosCirFilesInput!: ElementRef;
  @ViewChild('pedidosMatFilesInput') pedidosMatFilesInput!: ElementRef;
  @ViewChild('estudiosCircFilesInput') estudiosCircFilesInput!: ElementRef;
  @ViewChild('pedidosOxiFilesInput') pedidosOxiFilesInput!: ElementRef;

  // Almacenamiento para archivos seleccionados
  selectedFiles: {
    recetasFiles: File[],
    pedidosEstFiles: File[],
    pedidosCirFiles: File[],
    pedidosMatFiles: File[],
    estudiosCircFiles: File[],
    pedidosOxiFiles: File[]
  } = {
      recetasFiles: [],
      pedidosEstFiles: [],
      pedidosCirFiles: [],
      pedidosMatFiles: [],
      estudiosCircFiles: [],
      pedidosOxiFiles: []
    };

  constructor(
    private fb: FormBuilder,
    private autorizacionesService: AutorizacionesService
  ) {
    const hoy = new Date();
    this.fechaMinima = hoy.toISOString().split('T')[0];
  }

  // ==========================================
  // CICLO DE VIDA DEL COMPONENTE
  // ==========================================
  ngOnInit() {
    this.inicializarFormulario();
    this.configurarEscuchadoresDelFormulario();
    this.cargarDatosIniciales();
  }

  // ==========================================
  // INICIALIZACIÓN DEL FORMULARIO
  // ==========================================
  private inicializarFormulario(): void {
    this.authForm = this.fb.group({
      // Datos personales
      tipoDocumento: ['DNI', Validators.required],
      dni: ['', [Validators.required, documentNumberValidator()]],
      apellido: ['', [Validators.required, onlyLettersValidator()]],
      nombre: ['', [Validators.required, onlyLettersValidator()]],
      email: ['', [Validators.required, Validators.email]],
      codigoArea: ['', [Validators.required, Validators.minLength(3), phoneNumberValidator()]],
      numeroTelefono: ['', [Validators.required, Validators.minLength(6), phoneNumberValidator()]],

      // Datos para solicitar la autorización
      tipoAutorizacion: ['', Validators.required],

      // Medicación
      diagnosticoMed: [''],
      prescripMedicPrim: [''],
      farmacia: [''],
      recetasFiles: [null],
      observacionesMed: [''],

      // Estudios
      diagnosticoEst: [''],
      tieneTurno: [''],
      fechaTurno: [''],
      institucion: [''],
      pedidosEstFiles: [null],
      observacionesEst: [''],

      // Cirugía
      diagnosticoCir: [''],
      necesitaMateriales: [''],
      tieneTurnoCir: [''],
      fechaTurnoCir: [''],
      institucionCir: [''],
      pedidosCirFiles: [null],
      pedidosMatFiles: [null],
      estudiosCircFiles: [null],
      observacionesCir: [''],

      // Oxígeno
      diagnosticoOxi: [''],
      duracionTratamiento: [''],
      proveedor: [''],
      pedidosOxiFiles: [null],
      observacionesOxi: ['']
    });
  }

  private configurarEscuchadoresDelFormulario(): void {
    // Observer para cambios en tipo de autorización
    this.authForm.get('tipoAutorizacion')?.valueChanges.subscribe(tipo => {
      this.actualizarValidaciones(tipo);
    });

    // Observer para cambios en tipo de documento
    this.authForm.get('tipoDocumento')?.valueChanges.subscribe(() => {
      const dniControl = this.authForm.get('dni');
      if (dniControl) {
        dniControl.updateValueAndValidity();
        // Resetear la validación cuando cambia el tipo de documento
        this.resetDocumentoValidado();
      }
    });

    // Observer para cambios en el número de documento
    this.authForm.get('dni')?.valueChanges.subscribe(() => {
      // Resetear la validación cuando cambia el número de documento
      this.resetDocumentoValidado();
    });
  }

  private cargarDatosIniciales(): void {
    this.cargarAutorizacion();
    this.cargarFarmacias();
    this.cargarInstituciones();
  }

  // ==========================================
  // CARGA DE DATOS
  // ==========================================
  cargarAutorizacion(): void {
    this.autorizacionesService.obtenerAutorizaciones().subscribe(
      (data) => {
        this.autorizaciones = data.sort((a, b) => a.id - b.id);
      },
      (error) => {
        console.error('Error al cargar autorizaciones', error);
      }
    );
  }

  cargarFarmacias(): void {
    this.autorizacionesService.obtenerFarmacias().subscribe(
      (data) => {
        // El orden lo define el backend (prioridad ORDEN_VISUALIZACION + resto por localidad/sucursal)
        this.farmacias = [...data];
      },
      (error) => {
        console.error('Error al cargar farmacias', error);
      }
    );
  }

  cargarInstituciones(): void {
  this.autorizacionesService.obtenerInstituciones().subscribe(
    (data) => {
      this.instituciones = data;
    },
    (error) => {
      console.error('Error al cargar instituciones', error);
    }
  );
}

  // ==========================================
  // GESTIÓN DE VALIDACIONES DEL FORMULARIO
  // ==========================================
  tipoSeleccionado() {
    return Number(this.authForm.get('tipoAutorizacion')?.value);
  }

  actualizarValidaciones(tipo: Number) {
    // Limpiar validaciones condicionales
    this.limpiarValidaciones();

    // Configurar validaciones según el tipo seleccionado
    switch (tipo) {
      case 1:
        this.authForm.get('diagnosticoMed')?.setValidators(Validators.required);
        this.authForm.get('prescripMedicPrim')?.setValidators(Validators.required);
        this.authForm.get('farmacia')?.setValidators(Validators.required);
        this.authForm.get('recetasFiles')?.setValidators(Validators.required);
        break;

      case 2:
        this.authForm.get('diagnosticoEst')?.setValidators(Validators.required);
        this.authForm.get('tieneTurno')?.setValidators(Validators.required);
        this.authForm.get('institucion')?.setValidators(Validators.required);
        this.authForm.get('pedidosEstFiles')?.setValidators(Validators.required);
        break;

      case 3:
        this.authForm.get('diagnosticoCir')?.setValidators(Validators.required);
        this.authForm.get('necesitaMateriales')?.setValidators(Validators.required);
        this.authForm.get('tieneTurnoCir')?.setValidators(Validators.required);
        this.authForm.get('institucionCir')?.setValidators(Validators.required);
        this.authForm.get('pedidosCirFiles')?.setValidators(Validators.required);
        this.authForm.get('pedidosMatFiles')?.setValidators(Validators.required);
        this.authForm.get('estudiosCircFiles')?.setValidators(Validators.required);
        break;

      case 4:
        this.authForm.get('diagnosticoOxi')?.setValidators(Validators.required);
        this.authForm.get('duracionTratamiento')?.setValidators(Validators.required);
        this.authForm.get('proveedor')?.setValidators(Validators.required);
        this.authForm.get('pedidosOxiFiles')?.setValidators(Validators.required);
        break;
    }

    // Actualizar la validez del formulario
    this.authForm.updateValueAndValidity();
  }

  limpiarValidaciones() {
    // Medicación
    this.authForm.get('diagnosticoMed')?.clearValidators();
    this.authForm.get('prescripMedicPrim')?.clearValidators();
    this.authForm.get('farmacia')?.clearValidators();
    this.authForm.get('recetasFiles')?.clearValidators();

    // Estudios
    this.authForm.get('diagnosticoEst')?.clearValidators();
    this.authForm.get('tieneTurno')?.clearValidators();
    this.authForm.get('fechaTurno')?.clearValidators();
    this.authForm.get('institucion')?.clearValidators();
    this.authForm.get('pedidosEstFiles')?.clearValidators();

    // Cirugía
    this.authForm.get('diagnosticoCir')?.clearValidators();
    this.authForm.get('necesitaMateriales')?.clearValidators();
    this.authForm.get('tieneTurnoCir')?.clearValidators();
    this.authForm.get('fechaTurnoCir')?.clearValidators();
    this.authForm.get('institucionCir')?.clearValidators();
    this.authForm.get('pedidosCirFiles')?.clearValidators();
    this.authForm.get('pedidosMatFiles')?.clearValidators();
    this.authForm.get('estudiosCircFiles')?.clearValidators();

    // Oxígeno
    this.authForm.get('diagnosticoOxi')?.clearValidators();
    this.authForm.get('duracionTratamiento')?.clearValidators();
    this.authForm.get('proveedor')?.clearValidators();
    this.authForm.get('pedidosOxiFiles')?.clearValidators();
  }

  // ==========================================
  // MANEJADORES DE EVENTOS DEL FORMULARIO
  // ==========================================
  onTipoChange(event: any) {
    const valor = Number(event.target.value);
    this.resetSelectedFiles();

    // Resetea los valores de los campos no visibles
    if (this.authForm) {
      // Resetear campos de medicación si no se selecciona medicación
      if (valor !== 1) {
        this.authForm.get('diagnosticoMed')?.reset();
        this.authForm.get('prescripMedicPrim')?.reset();
        this.authForm.get('farmacia')?.reset('');
        this.authForm.get('observacionesMed')?.reset();
      }

      // Resetear campos de estudios si no se selecciona estudios
      if (valor !== 2) {
        this.authForm.get('diagnosticoEst')?.reset();
        this.authForm.get('tieneTurno')?.reset();
        this.authForm.get('fechaTurno')?.reset();
        this.authForm.get('institucion')?.reset('');
        this.authForm.get('observacionesEst')?.reset();
      }

      // Resetear campos de cirugía si no se selecciona cirugía
      if (valor !== 3) {
        this.authForm.get('diagnosticoCir')?.reset();
        this.authForm.get('necesitaMateriales')?.reset();
        this.authForm.get('tieneTurnoCir')?.reset();
        this.authForm.get('fechaTurnoCir')?.reset();
        this.authForm.get('institucionCir')?.reset('');
        this.authForm.get('observacionesCir')?.reset();
      }

      // Resetear campos de oxígeno si no se selecciona oxígeno
      if (valor !== 4) {
        this.authForm.get('diagnosticoOxi')?.reset();
        this.authForm.get('duracionTratamiento')?.reset();
        this.authForm.get('proveedor')?.reset('');
        this.authForm.get('observacionesOxi')?.reset();
      }
    }

    // Llamar a actualizar las validaciones de acuerdo con la opción seleccionada
    this.actualizarValidaciones(valor);
  }

  onTurnoChange(event: any) {
    const value = event.target.value;
    if (value === 'S') {
      this.authForm.get('fechaTurno')?.setValidators([
        Validators.required,
        dateNotInPastValidator()
      ]);
    } else {
      this.authForm.get('fechaTurno')?.clearValidators();
    }
    this.authForm.get('fechaTurno')?.updateValueAndValidity();
  }

  onTurnoCirChange(event: any) {
    const value = event.target.value;
    if (value === 'S') {
      this.authForm.get('fechaTurnoCir')?.setValidators([
        Validators.required,
        dateNotInPastValidator()
      ]);
    } else {
      this.authForm.get('fechaTurnoCir')?.clearValidators();
    }
    this.authForm.get('fechaTurnoCir')?.updateValueAndValidity();
  }

  onNecesitaMaterialesChange(event: any) {
    const valor = event.target.value;

    if (valor === 'S') {
      this.authForm.get('pedidosMatFiles')?.setValidators(Validators.required);
    } else {
      this.authForm.get('pedidosMatFiles')?.clearValidators();

      this.selectedFiles.pedidosMatFiles = [];
      if (this.pedidosMatFilesInput) {
        this.pedidosMatFilesInput.nativeElement.value = '';
      }
    }

    this.authForm.get('pedidosMatFiles')?.updateValueAndValidity();
  }

  /**
   * Wrapper para la función de utilidad que maneja Enter como Tab
   */
  manejarEnterComoTab(event: KeyboardEvent): void {
    FormularioUtils.manejarEnterComoTab(event);
  }

  // ==========================================
  // MÉTODOS DE GESTIÓN DE ARCHIVOS
  // ==========================================
  resetSelectedFiles() {
    this.selectedFiles = {
      recetasFiles: [],
      pedidosEstFiles: [],
      pedidosCirFiles: [],
      pedidosMatFiles: [],
      estudiosCircFiles: [],
      pedidosOxiFiles: [],
    };

    // También resetea los inputs si los estás usando con template reference (#fileInput)
    if (this.recetasFilesInput) this.recetasFilesInput.nativeElement.value = '';
    if (this.pedidosEstFilesInput) this.pedidosEstFilesInput.nativeElement.value = '';
    if (this.pedidosCirFilesInput) this.pedidosCirFilesInput.nativeElement.value = '';
    if (this.pedidosMatFilesInput) this.pedidosMatFilesInput.nativeElement.value = '';
    if (this.estudiosCircFilesInput) this.estudiosCircFilesInput.nativeElement.value = '';
    if (this.pedidosOxiFilesInput) this.pedidosOxiFilesInput.nativeElement.value = '';
  }

  onFileSelected(event: any, fileType: keyof typeof this.selectedFiles) {
    if (event.target.files && event.target.files.length > 0) {
      const files: FileList = event.target.files;
      const nuevosArchivos = Array.from(files);

      // Calcular tamaño actual de todos los archivos
      const tamanioActual = this.calcularTamanioTotalArchivos();

      // Calcular tamaño de los nuevos archivos
      const tamanioNuevos = nuevosArchivos.reduce((total, file) => total + file.size, 0);

      // Verificar si supera el límite
      if (tamanioActual + tamanioNuevos > this.MAX_TOTAL_FILE_SIZE) {
        Swal.fire({
          icon: 'error',
          title: 'Tamaño excedido',
          html: `El tamaño total de los archivos no puede superar 10 MB.<br>`,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Entendido'
        });

        // Limpiar el input
        event.target.value = '';
        return;
      }

      // Agregar los nuevos archivos a los existentes
      this.selectedFiles[fileType] = [...this.selectedFiles[fileType], ...nuevosArchivos];

      // Limpiar el input de archivos para permitir seleccionar el mismo archivo nuevamente
      event.target.value = '';

      // Actualizar el valor del control para validación
      this.actualizarEstadoArchivoControl(fileType);
    }
  }

 /**
 * Calcula el tamaño total de todos los archivos seleccionados
 */
  calcularTamanioTotalArchivos(): number {
    let tamanioTotal = 0;

    Object.keys(this.selectedFiles).forEach(key => {
      const archivos = this.selectedFiles[key as keyof typeof this.selectedFiles];
      tamanioTotal += archivos.reduce((total: number, file: File) => total + file.size, 0);
    });

    return tamanioTotal;
  }

  /**
   * Obtiene el tamaño total formateado para mostrar
   */
  getTamanioTotalFormateado(): string {
    const tamanio = this.calcularTamanioTotalArchivos();
    return this.formatFileSize(tamanio);
  }

  eliminarArchivo(fileType: keyof typeof this.selectedFiles, index: number) {
    // Crear una copia del array actual sin el archivo en la posición indicada
    this.selectedFiles[fileType] = [
      ...this.selectedFiles[fileType].slice(0, index),
      ...this.selectedFiles[fileType].slice(index + 1)
    ];

    // Actualizar el valor del control para validación
    this.actualizarEstadoArchivoControl(fileType);
  }

  private actualizarEstadoArchivoControl(fileType: keyof typeof this.selectedFiles) {
    const filesArray = this.selectedFiles[fileType];
    const control = this.authForm.get(fileType);

    if (filesArray.length > 0) {
      control?.setValue('files-selected');
      control?.setErrors(null); // Eliminar cualquier error existente
    } else {
      control?.setValue(null);
      // Si es obligatorio y ya se intentó enviar, marcar con error
      if (control?.hasValidator(Validators.required) && this.submitted) {
        control?.setErrors({ required: true });
      }
    }

    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  formatFileSize(size: number): string {
    if (size < 1024) {
      return size + ' bytes';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(1) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(1) + ' MB';
    }
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    switch (extension) {
      case 'pdf':
        return 'bi bi-file-earmark-pdf';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'bi bi-file-earmark-image';
      default:
        return 'bi bi-file-earmark';
    }
  }

  // ==========================================
  // FUNCIONES AUXILIARES DE VALIDACIÓN
  // ==========================================
  isInvalid(controlName: string): boolean {
    const control = this.authForm.get(controlName);

    if (controlName.endsWith('Files')) {
      const tipo = Number(this.authForm.get('tipoAutorizacion')?.value);

      let esRequerido = false;

      switch (tipo) {
        case 1:
          esRequerido = controlName === 'recetasFiles';
          break;
        case 2:
          esRequerido = controlName === 'pedidosEstFiles';
          break;
        case 3:
          if (controlName === 'pedidosMatFiles') {
            esRequerido = this.authForm.get('necesitaMateriales')?.value === 'S';
          } else {
            esRequerido = ['pedidosCirFiles', 'estudiosCircFiles'].includes(controlName);
          }
          break;
        case 4:
          esRequerido = controlName === 'pedidosOxiFiles';
          break;
      }

      if (esRequerido && (this.submitted || (control?.touched))) {
        return this.selectedFiles[controlName as keyof typeof this.selectedFiles].length === 0;
      }

      return false;
    }

    return !!control && control.invalid && (control.dirty || control.touched || this.submitted);
  }

  getNumeroTelefonoErrorMessage(): string {
    const telefonoControl = this.authForm.get('numeroTelefono');
    if (telefonoControl?.hasError('required')) {
      return 'Este campo es obligatorio';
    }
    if (telefonoControl?.hasError('minlength')) {
      return 'El número debe tener al menos 6 dígitos';
    }
    if (telefonoControl?.hasError('allSameDigits')) {
      return 'El número no puede tener todos los dígitos iguales';
    }
    return '';
  }

  getDocumentNumberErrorMessage(): string {
    const dniControl = this.authForm.get('dni');
    if (dniControl?.hasError('required')) {
      return 'Este campo es obligatorio';
    }
    if (dniControl?.hasError('invalidDocumentNumber')) {
      const tipoDocumento = this.authForm.get('tipoDocumento')?.value;
      switch (tipoDocumento) {
        case 'DNI':
          return 'El DNI debe tener entre 7 y 9 dígitos numéricos';
        case 'CUIT':
        case 'CUIL':
          return 'El CUIT/CUIL debe tener 11 dígitos numéricos';
        case 'PASAPORTE':
          return 'Ingrese únicamente dígitos numéricos del pasaporte (entre 6 y 9 dígitos)';
        default:
          return 'Número de documento inválido';
      }
    }
    return '';
  }

  /**
   * Verifica si el formulario es válido para habilitar el botón de envío
   */
  esFormularioValido(): boolean {
    return !this.authForm.invalid &&
      !FormularioUtils.hayArchivosObligatoriosFaltantes(this.authForm, this.selectedFiles);
  }
  // ==========================================
  // ENVÍO DEL FORMULARIO
  // ==========================================
  enviarSolicitud() {
    const esValido = this.esFormularioValido;

    if (!esValido) {
      Object.keys(this.authForm.controls).forEach(key => {
        const control = this.authForm.get(key);
        control?.markAsTouched();
      });

      this.enfocarPrimerElementoInvalido();

      Swal.fire({
        icon: 'warning',
        title: 'Formulario incompleto',
        text: 'Por favor completa todos los campos obligatorios marcados con *',
        confirmButtonColor: '#3085d6',
        returnFocus: false
      });
      return;
    }

    Swal.fire({
      title: '¿Confirmar envío?',
      text: 'La solicitud será procesada en un plazo de hasta 72 horas hábiles',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.submitted = true;

        FormularioUtils.procesarEnvioCompleto(
          this.authForm,
          this.farmacias,
          this.instituciones,
          this.selectedFiles,
          this.autorizacionesService,
          () => FormularioUtils.mostrarExito(),
          () => this.resetForm()
        );
      }
    });
  }

  onSubmit() {
    const backendData = FormularioUtils.mapFormToBackendModel(
      this.authForm.value,
      this.farmacias,
      this.instituciones
    );

    this.autorizacionesService.enviarSolicitud(backendData).subscribe({
      next: res => console.log('Formulario enviado', res),
      error: err => console.error('Error al enviar formulario', err)
    });
  }

  // ==========================================
  // MÉTODOS RELACIONADOS CON LA INTERFAZ
  // ==========================================
  mostrarExito() {
    Swal.fire({
      title: '¡Solicitud enviada!',
      text: 'Recibirás una respuesta por correo electrónico en un plazo de hasta 72 horas hábiles.',
      icon: 'success',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3085d6'
    });
  }

  mostrarError(mensaje: string = 'Ocurrió un error al enviar la solicitud. Por favor, inténtalo nuevamente.') {
    Swal.fire({
      title: 'Error',
      text: mensaje,
      icon: 'error',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#d33'
    });
  }

  enfocarPrimerElementoInvalido() {
    // Primero verificamos campos regulares del formulario (inputs, selects, etc.)
    const formControls = Object.keys(this.authForm.controls);

    for (const controlName of formControls) {
      const control = this.authForm.get(controlName);

      if (control?.invalid) {
        // Buscar el elemento DOM correspondiente a este control
        const selector = `[formControlName="${controlName}"]`;
        const element = document.querySelector(selector) as HTMLElement;

        if (element) {
          // Scroll suave hacia el elemento
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Establecer foco después de un breve retraso para asegurar que el scroll se complete
          setTimeout(() => {
            element.focus();
          }, 500);

          return; // Salir después de encontrar y enfocar el primer elemento inválido
        }
      }
    }
  }

  resetForm() {
    this.authForm.reset();
    this.submitted = false;
    this.uploadProgress = 0;

    this.authForm.get('tipoDocumento')?.setValue('DNI');
    this.authForm.get('tipoAutorizacion')?.setValue('');
    this.authForm.get('farmacia')?.setValue('');
    this.authForm.get('institucion')?.setValue('');
    this.authForm.get('institucionCir')?.setValue('');
    this.authForm.get('proveedor')?.setValue('');
    this.authForm.get('duracionTratamiento')?.setValue('');

    this.resetSelectedFiles();
  }

  buscarAfiliado() {
    var tipoDocumento = this.authForm.get('tipoDocumento')?.value;
    const documento = this.authForm.get('dni')?.value;

    if (!documento || this.authForm.get('dni')?.invalid) {
      return;
    }

    if (tipoDocumento === 'DNI') {
      tipoDocumento = 'D.N.I.';
    }

    this.autorizacionesService.buscarAfiliado(tipoDocumento, documento).subscribe({
      next: (afiliado) => {
        // Asigna los valores al formulario
        this.authForm.get('nombre')?.setValue(afiliado.nombre);
        this.authForm.get('apellido')?.setValue(afiliado.apellido);
        //this.authForm.get('email')?.setValue(afiliado.email);

        //const telefonoStr = afiliado.telefono;

        /*if (telefonoStr && telefonoStr.length > 0) {
          // Separo los primeros 4 dígitos como código de área (ejemplo: 3541)
          if (telefonoStr.length >= 4) {
            this.authForm.get('codigoArea')?.setValue(telefonoStr.substring(0, 4));
            this.authForm.get('numeroTelefono')?.setValue(telefonoStr.substring(4));
          } else {
            this.authForm.get('codigoArea')?.setValue('');
            this.authForm.get('numeroTelefono')?.setValue(telefonoStr);
          }
        } else {
          // Si no hay teléfono, dejar ambos campos vacíos
          this.authForm.get('codigoArea')?.setValue('');
          this.authForm.get('numeroTelefono')?.setValue('');
        }*/
      },
      error: () => {
        this.authForm.get('dni')?.setValue('');
      }
    });
  }

  // Método para resetear la validación cuando se cambia el documento
  resetDocumentoValidado() {
    this.documentoValidado = false;
  }
  getFechaTurnoErrorMessage(campo: 'fechaTurno' | 'fechaTurnoCir'): string {
    const control = this.authForm.get(campo);
    if (control?.hasError('required')) {
      return 'Por favor seleccione una fecha';
    }
    if (control?.hasError('dateInPast')) {
      return 'La fecha no puede ser anterior a hoy';
    }
    return '';
  }
}